import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTrash, faSpinner, faCheckCircle, faExclamationTriangle, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { apiService } from '@/utils/api';
import { Site, LedyerAuthentication as LedyerAuthType } from '@/types';

const LedyerAuthentication: React.FC = () => {
  const [formData, setFormData] = useState({
    site_id: '',
    api_id: '',
    api_secret: '',
    live_mode: false,
  });

  const [showApiSecret, setShowApiSecret] = useState(false);
  const [alerts, setAlerts] = useState({
    saveSuccess: false,
    deleteSuccess: false,
    error: false,
  });

  const queryClient = useQueryClient();

  // Fetch sites
  const { data: sites = [] } = useQuery('sites', apiService.getSites);
  const siteId = sites.length > 0 ? sites[0].id : null;

  // Fetch Ledyer auth settings
  const { data: authSettings, isLoading } = useQuery(
    ['ledyerAuth', siteId],
    () => apiService.getLedyerAuths(siteId!),
    {
      enabled: !!siteId,
      onSuccess: (data: LedyerAuthType) => {
        if (data) {
          setFormData({
            site_id: data.site_id?.toString() || '',
            api_id: data.api_id || '',
            api_secret: data.api_secret || '',
            live_mode: data.live_mode || false,
          });
        }
      },
    }
  );

  // Save mutation
  const saveMutation = useMutation(
    (data: any) => {
      if (authSettings?.id) {
        return apiService.updateLedyerAuth(siteId!, data);
      } else {
        return apiService.createLedyerAuth(siteId!, data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ledyerAuth', siteId]);
        showAlert('saveSuccess');
      },
      onError: (error) => {
        console.error('Save failed:', error);
        showAlert('error');
      },
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    () => apiService.deleteLedyerAuth(siteId!),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ledyerAuth', siteId]);
        setFormData({
          site_id: '',
          api_id: '',
          api_secret: '',
          live_mode: false,
        });
        showAlert('deleteSuccess');
      },
      onError: (error) => {
        console.error('Delete failed:', error);
        showAlert('error');
      },
    }
  );

  const showAlert = (type: 'saveSuccess' | 'deleteSuccess' | 'error') => {
    setAlerts(prev => ({
      ...prev,
      [type]: true,
    }));

    setTimeout(() => {
      setAlerts(prev => ({
        ...prev,
        [type]: false,
      }));
    }, 3000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const submitData = {
      ...formData,
      site_id: parseInt(formData.site_id),
    };

    saveMutation.mutate(submitData);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this integration?')) {
      deleteMutation.mutate();
    }
  };

  const toggleApiSecretVisibility = () => {
    setShowApiSecret(prev => !prev);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="section-title">Ledyer Authentication Settings</h1>
      </div>

      {/* Alerts */}
      {alerts.saveSuccess && (
        <div className="alert alert-success flex items-center space-x-2">
          <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4" />
          <span>Ledyer Authentication Settings saved</span>
        </div>
      )}

      {alerts.deleteSuccess && (
        <div className="alert alert-success flex items-center space-x-2">
          <FontAwesomeIcon icon={faCheckCircle} className="w-4 h-4" />
          <span>Ledyer Authentication Settings deleted</span>
        </div>
      )}

      {alerts.error && (
        <div className="alert alert-danger flex items-center space-x-2">
          <FontAwesomeIcon icon={faExclamationTriangle} className="w-4 h-4" />
          <span>Something went wrong</span>
        </div>
      )}

      <div className="card">
        <form onSubmit={handleSubmit} className="form-section">
          <div className="form-row">
            <div className="form-column">
              <div>
                <label className="form-label form-label-required">Site:</label>
                <select
                  value={formData.site_id}
                  onChange={(e) => handleInputChange('site_id', e.target.value)}
                  className="form-input"
                  required
                >
                  <option value="">Select Site</option>
                  {sites.map((site: Site) => (
                    <option key={site.id} value={site.id}>
                      {site.url}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="form-label form-label-required">API ID:</label>
                <input
                  type="text"
                  value={formData.api_id}
                  onChange={(e) => handleInputChange('api_id', e.target.value)}
                  className="form-input"
                  required
                  placeholder="Enter Ledyer API ID"
                />
              </div>
            </div>

            <div className="form-column">
              <div>
                <label className="form-label form-label-required">API Secret:</label>
                <div className="relative">
                  <input
                    type={showApiSecret ? 'text' : 'password'}
                    value={formData.api_secret}
                    onChange={(e) => handleInputChange('api_secret', e.target.value)}
                    className="form-input pr-10"
                    required
                    placeholder="Enter Ledyer API Secret"
                  />
                  <button
                    type="button"
                    onClick={toggleApiSecretVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <FontAwesomeIcon
                      icon={showApiSecret ? faEyeSlash : faEye}
                      className="w-4 h-4"
                    />
                  </button>
                </div>
              </div>

              <div className="flex items-center pt-4">
                <input
                  type="checkbox"
                  id="live_mode"
                  checked={formData.live_mode}
                  onChange={(e) => handleInputChange('live_mode', e.target.checked)}
                  className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                />
                <label htmlFor="live_mode" className="ml-2 text-sm font-medium text-gray-700">
                  Live Mode
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-6 space-x-3">
            {authSettings?.id && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
                className="btn btn-danger flex items-center space-x-2"
              >
                {deleteMutation.isLoading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                    <span>Delete</span>
                  </>
                )}
              </button>
            )}

            <button
              type="submit"
              disabled={saveMutation.isLoading}
              className="px-4 py-2 text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
              style={{ backgroundColor: 'hsl(15 100% 52%)' }}
            >
              {saveMutation.isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faSave} className="w-4 h-4" />
                  <span>Save</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LedyerAuthentication;
